import {TIPOS_DE_ALERTA_SUGERIDOS, unirValores, valoresDeFiltro} from '@cnc/contracts';
import {useId, useState, type FormEvent} from 'react';
import {useAlertasPropias, useCrearAlerta} from '../api/alertas';
import {ErrorDeApi} from '../api/cliente';
import {tr} from '../i18n/idioma';
import {SelectorMultiple} from './selector-multiple';

// Crear una alerta propia (decision 0037): sin exportaciones queda abierta
// (general); con una o varias, se ve tambien en el detalle de cada una.
export function FormularioDeAlerta({
  hoy,
  codigos: iniciales = [],
  onListo,
}: {
  hoy: string;
  // Exportaciones ya elegidas (por ejemplo, desde el detalle de una exportacion).
  codigos?: string[];
  onListo: () => void;
}) {
  const crear = useCrearAlerta();
  const opciones = useAlertasPropias('activa');
  const idTipos = useId();
  const [titulo, setTitulo] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [diasAntes, setDiasAntes] = useState('3');
  const [tipo, setTipo] = useState('General');
  const [notas, setNotas] = useState('');
  const [codigos, setCodigos] = useState<string[]>(iniciales);
  const [error, setError] = useState<string | null>(null);

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await crear.mutateAsync({
        titulo,
        fecha,
        diasAntes: Math.max(0, Math.round(Number(diasAntes) || 0)),
        tipoEvento: tipo.trim() || 'General',
        notas: notas.trim() || null,
        codigos,
      });
      onListo();
    } catch (falla) {
      setError(
        falla instanceof ErrorDeApi ? falla.message : tr('No se pudo crear la alerta.', 'Could not create the alert.'),
      );
    }
  };

  const campo = 'mt-0.5 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta';
  return (
    <form
      onSubmit={e => void guardar(e)}
      aria-label={tr('Nueva alerta', 'New alert')}
      className="space-y-2 rounded-md border border-verde/40 bg-verde-claro/30 p-3 text-sm"
    >
      <label className="block text-xs text-apagado">
        {tr('Qué hay que recordar', 'What to remember')}
        <input
          required
          maxLength={200}
          value={titulo}
          onChange={e => setTitulo(e.target.value)}
          className={campo}
          placeholder={tr('Ej.: Pedir el BL a la naviera', 'E.g.: Ask the shipping line for the BL')}
        />
      </label>
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="block text-xs text-apagado">
          {tr('Fecha', 'Date')}
          <input type="date" required value={fecha} onChange={e => setFecha(e.target.value)} className={campo} />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Avisar días antes', 'Warn days before')}
          <input
            type="number"
            min={0}
            max={365}
            value={diasAntes}
            onChange={e => setDiasAntes(e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Tipo', 'Type')}
          <input list={idTipos} maxLength={60} value={tipo} onChange={e => setTipo(e.target.value)} className={campo} />
          <datalist id={idTipos}>
            {TIPOS_DE_ALERTA_SUGERIDOS.map(t => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </label>
      </div>
      <SelectorMultiple
        etiqueta={tr('Exportaciones (ninguna = alerta abierta)', 'Exports (none = open alert)')}
        valor={unirValores(codigos)}
        opciones={(opciones.data?.exportaciones ?? []).map(x => ({
          valor: x.codigo,
          etiqueta: `${x.codigo} · ${x.estado}`,
        }))}
        onCambiar={v => setCodigos(valoresDeFiltro(v))}
        textoVacio={tr('Ninguna (abierta)', 'None (open)')}
      />
      <label className="block text-xs text-apagado">
        {tr('Notas', 'Notes')}
        <textarea rows={2} maxLength={2000} value={notas} onChange={e => setNotas(e.target.value)} className={campo} />
      </label>
      {error && (
        <p className="text-alerta" role="alert">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={crear.isPending}
          className="rounded-md bg-verde px-3 py-1.5 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {tr('Crear alerta', 'Create alert')}
        </button>
        <button type="button" onClick={onListo} className="rounded-md border border-linea px-3 py-1.5 hover:bg-papel">
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}
